/** @jsxImportSource @emotion/react */

import { useContext, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { AlternateAction } from 'components/AlternateAction';
import { ErrorBox } from 'components/ErrorBox';
import { hasMinLength, Input, isRequired, Submit } from 'components/Form';
import { PageHeading } from 'components/Page';
import { Text } from 'components/Text';
import { TrackingContext } from 'context/Tracking';
import { UserContext } from 'context/User';
import { useMfaRedirect } from 'hooks/useMfaRedirect';
import { mfaRecoveryRoutes } from 'pages/MfaRecovery/routes';
import { signInRoutes } from 'pages/SignIn/routes';
import { Endpoints, post } from 'utils/api';
import { useHistory } from 'utils/router';
import { useStyles } from 'utils/useStyles';

import { mfaLoginStyles } from './MfaLogin.styles';

type VerificationMode = 'totp' | 'recovery';

interface TotpFormValues {
  code: string;
}

interface RecoveryFormValues {
  recoveryCode: string;
}

interface MfaVerifyLoginResponse {
  success: boolean;
}

const formName = 'mfa-login';

const formatTotpCode = (value: string): string => {
  return value.replace(/\D/g, '').slice(0, 6);
};

export const MfaLoginForm = () => {
  const styles = useStyles(mfaLoginStyles);
  const [, { faro, resetUser }] = useContext(TrackingContext);
  const [, { setUser }] = useContext(UserContext);
  const history = useHistory();
  const redirectAfterSuccess = useMfaRedirect();

  const [mode, setMode] = useState<VerificationMode>('totp');
  const [serverError, setServerError] = useState<string>();

  const {
    control: totpControl,
    handleSubmit: handleTotpFormSubmit,
    setValue: setTotpValue,
    formState: { isSubmitting: isTotpSubmitting, errors: totpErrors },
  } = useForm<TotpFormValues>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: { code: '' },
  });

  const {
    control: recoveryControl,
    handleSubmit: handleRecoveryFormSubmit,
    setValue: setRecoveryValue,
    formState: { isSubmitting: isRecoverySubmitting, errors: recoveryErrors },
  } = useForm<RecoveryFormValues>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: { recoveryCode: '' },
  });

  const isSubmitting = isTotpSubmitting || isRecoverySubmitting;

  const handleTotpSubmit: SubmitHandler<TotpFormValues> = async (values) => {
    setServerError(undefined);

    try {
      faro.trackFormSubmit(formName);

      await post<MfaVerifyLoginResponse>(Endpoints.MFA_VERIFY_LOGIN, {
        token: values.code,
      });

      faro.trackFormSubmitSuccess(formName);
      await redirectAfterSuccess();
    } catch (err: unknown) {
      const error = err as { message?: string };
      const errorMessage = error.message || 'Invalid verification code. Please try again.';

      faro.trackFormSubmitError(formName, errorMessage);
      faro.trackError(err as Error);
      setServerError(errorMessage);
      setTotpValue('code', '');
    }
  };

  const handleRecoverySubmit: SubmitHandler<RecoveryFormValues> = async (values) => {
    setServerError(undefined);

    try {
      faro.trackFormSubmit(`${formName}-recovery`);

      await post<MfaVerifyLoginResponse>(Endpoints.MFA_VERIFY_LOGIN, {
        recoveryCode: values.recoveryCode,
      });

      faro.trackFormSubmitSuccess(`${formName}-recovery`);
      await redirectAfterSuccess();
    } catch (err: unknown) {
      const error = err as { message?: string };
      const errorMessage = error.message || 'Invalid recovery code. Please try again.';

      faro.trackFormSubmitError(`${formName}-recovery`, errorMessage);
      faro.trackError(err as Error);
      setServerError(errorMessage);
      setRecoveryValue('recoveryCode', '');
    }
  };

  const handleModeToggle = () => {
    setServerError(undefined);
    const newMode = mode === 'totp' ? 'recovery' : 'totp';
    setMode(newMode);
    faro.trackEvent(`${formName}-mode-toggle`, { newMode });
  };

  const handleBackToSignIn = async () => {
    faro.trackEvent(`${formName}-back-to-signin`);
    resetUser();
    await setUser(null);
    history.push(signInRoutes.base);
  };

  return (
    <div css={styles.container}>
      <PageHeading css={styles.title}>
        {mode === 'totp' ? 'Enter verification code' : 'Enter recovery code'}
      </PageHeading>
      <Text align="center" css={styles.description} type="normal">
        {mode === 'totp'
          ? 'Enter the 6-digit code from your authenticator app.'
          : 'Enter one of your recovery codes to sign in.'}
      </Text>

      {mode === 'totp' ? (
        <form key="totp-form" onSubmit={handleTotpFormSubmit(handleTotpSubmit)} noValidate css={styles.form}>
          <div css={styles.codeInputContainer}>
            <Input
              control={totpControl}
              errors={totpErrors}
              name="code"
              label="Verification code"
              type="text"
              showLabel
              autoFocus
              disabled={isSubmitting}
              formatter={formatTotpCode}
              placeholder="000000"
              validate={[isRequired('Please enter a verification code'), hasMinLength(6, 'Code must be 6 digits')]}
            />
            <Submit
              id="submit-totp"
              label="Verify"
              isLoading={isSubmitting}
              loadingText="Verifying..."
              disabled={isSubmitting}
              trackingLabel={formName}
            />
          </div>
          {serverError && <ErrorBox id="server-error">{serverError}</ErrorBox>}
        </form>
      ) : (
        <form key="recovery-form" onSubmit={handleRecoveryFormSubmit(handleRecoverySubmit)} noValidate css={styles.form}>
          <div css={[styles.codeInputContainer, styles.recoveryCodeInput]}>
            <Input
              control={recoveryControl}
              errors={recoveryErrors}
              name="recoveryCode"
              label="Recovery code"
              type="text"
              showLabel
              autoFocus
              disabled={isSubmitting}
              placeholder="Enter recovery code"
              validate={[isRequired('Please enter a recovery code')]}
            />
            <Submit
              id="submit-recovery"
              label="Verify"
              isLoading={isSubmitting}
              loadingText="Verifying..."
              disabled={isSubmitting}
              trackingLabel={`${formName}-recovery`}
            />
          </div>
          {serverError && <ErrorBox id="server-error">{serverError}</ErrorBox>}
        </form>
      )}

      <div css={styles.actionsContainer}>
        <AlternateAction
          label={mode === 'totp' ? 'Lost your device?' : 'Have your device?'}
          actionLabel={mode === 'totp' ? 'Use recovery code' : 'Use authenticator app'}
          actionEvent={handleModeToggle}
          actionType="button"
          id="mode-toggle"
        />
        <AlternateAction
          label=""
          actionLabel="Back to sign in"
          actionEvent={handleBackToSignIn}
          actionType="button"
          id="back-to-signin"
        />
        <AlternateAction
          label="Having trouble?"
          actionLabel="Get help signing in"
          actionLink={mfaRecoveryRoutes.base}
          id="mfa-recovery-link"
        />
      </div>
    </div>
  );
};
